import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import Layout from './components/Layout';
import ResponsiveShadow from './components/ResponsiveShadow';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import Toolbar from './components/Toolbar';
import TableContainer from './components/TableContainer';
import TableHeader from './components/TableHeader';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import UploadImagemModal from './components/modalServices/UploadImagemModal';
import ImageViewModal from './components/modals/ImageViewModal';
import useImageViewModal from './components/hooks/useImageViewModal';

const UploadImagem = () => {
  const [templates, setTemplates] = useState([]);
  const [loadingImage, setLoadingImage] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const { showImageModal, modalProps } = useImageViewModal();
  const [appCliente, setAppCliente] = useState(null);

  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  useEffect(() => {
    const appClienteData = localStorage.getItem('appCliente');
    if (!token) {
      navigate('/');
    } else if (appClienteData) {
      setAppCliente(JSON.parse(appClienteData));
    }
  }, [navigate, token]);

  useEffect(() => {
    if (appCliente) {
      fetchTemplates();
    }
  }, [appCliente]);

  // Limpeza das URLs blob quando o componente é desmontado
  useEffect(() => {
    return () => {
      templates.forEach(template => {
        if (
          template.thumbnailUrl &&
          template.thumbnailUrl.startsWith('blob:')
        ) {
          URL.revokeObjectURL(template.thumbnailUrl);
        }
      });
    };
  }, [templates]);

  const fetchTemplates = async () => {
    if (!appCliente?.cnpj) {
      console.error('CNPJ não encontrado em appCliente.');
      return;
    }
    try {
      const response = await axios.get(
        `${API_BASE_URL.APP_IMAGES}/list/${appCliente.cnpj}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Carregar miniaturas para cada template
      const templatesWithThumbnails = await Promise.all(
        response.data.map(async template => {
          try {
            const imageResponse = await axios.get(
              `${API_BASE_URL.APP_IMAGES}/view/${template.cnpj}/${template.orientacao}`,
              {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
              }
            );
            const thumbnailUrl = URL.createObjectURL(imageResponse.data);
            return { ...template, thumbnailUrl };
          } catch (error) {
            console.warn(
              `Erro ao carregar miniatura para ${template.orientacao}:`,
              error
            );
            return template; // Retorna sem a miniatura em caso de erro
          }
        })
      );

      setTemplates(templatesWithThumbnails);
    } catch (error) {
      console.error('Erro ao buscar os templates:', error);
    }
  };

  const handleUploadSuccess = message => {
    showDefaultModal('success', message);
    fetchTemplates();
    setShowUploadModal(false);
  };

  const handleDeleteClick = template => {
    showConfirmModal('delete', () => handleDeleteConfirm(template));
  };

  const handleDeleteConfirm = async templateToDelete => {
    if (templateToDelete) {
      try {
        await axios.delete(`${API_BASE_URL.APP_IMAGES}/delete`, {
          params: {
            cnpj: templateToDelete.cnpj,
            orientacao: templateToDelete.orientacao,
          },
          headers: { Authorization: `Bearer ${token}` },
        });

        // Limpar URL blob antes de recarregar
        if (
          templateToDelete.thumbnailUrl &&
          templateToDelete.thumbnailUrl.startsWith('blob:')
        ) {
          URL.revokeObjectURL(templateToDelete.thumbnailUrl);
        }

        showDefaultModal('success', 'Template excluído com sucesso.');
        fetchTemplates();
      } catch (error) {
        console.error('Erro ao excluir o template:', error);
        showDefaultModal('error', 'Erro ao excluir o template.');
      }
    }
  };

  const handleViewImage = async template => {
    setLoadingImage(true);

    // Mostra o modal com loading primeiro
    showImageModal({ loading: true }, 'Visualizar Imagem');

    try {
      // Faz o download da imagem via axios com autorização
      const response = await axios.get(
        `${API_BASE_URL.APP_IMAGES}/view/${template.cnpj}/${template.orientacao}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      // Converte o blob para URL temporária
      const imageUrl = URL.createObjectURL(response.data);

      // Atualiza o modal com a imagem carregada
      showImageModal(
        {
          ...template,
          imageUrl,
        },
        'Visualizar Imagem'
      );
    } catch (error) {
      console.error('Erro ao carregar imagem:', error);
      showDefaultModal('error', 'Erro ao carregar a imagem.');
    } finally {
      setLoadingImage(false);
    }
  };

  const getImageUrl = template => {
    return (
      template.imageUrl ||
      template.thumbnailUrl ||
      `${API_BASE_URL.APP_IMAGES}/view/${template.cnpj}/${template.orientacao}`
    );
  };

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader title="Cadastro de Imagens" icon="bi-image" closeRoute="/home" backButton={false} />
        <PageContent>
          <Toolbar
            actions={[
              { key: 'nova', label: 'Nova Imagem', icon: 'bi-plus-circle', variant: 'success', onClick: () => setShowUploadModal(true) },
            ]}
            searchControls={
              <span className="text-muted small">
                <i className="bi bi-images me-1"></i>
                Imagens Cadastradas: {templates.length}
              </span>
            }
          />
      <TableContainer
        style={{ width: '70%', margin: '0 auto', height: '300px' }}
        tableClass="table-custom-radius"
      >
        <TableHeader columns={[
          { label: <><i className="bi bi-image me-1"></i>Miniatura</>, align: 'center' },
          { label: <><i className="bi bi-aspect-ratio me-1"></i>Orientação</>, align: 'center' },
          { label: <><i className="bi bi-gear me-1"></i>Ações</>, align: 'center' },
        ]} />
        <tbody>
          {templates.length > 0 ? (
            templates.map((template, index) => (
              <tr key={index} style={{ height: '50%' }}>
                <td
                  className="text-center align-middle"
                  style={{ padding: '1rem' }}
                >
                  <img
                    src={getImageUrl(template)}
                    alt={`Miniatura ${
                      template.orientacao === 'P' ? 'Paisagem' : 'Retrato'
                    }`}
                    style={{
                      width: template.orientacao === 'P' ? '100px' : '70px',
                      height: template.orientacao === 'P' ? '70px' : '100px',
                      objectFit: 'cover',
                      objectPosition: 'top',
                      border: '2px solid #dee2e6',
                    }}
                    className="rounded shadow-sm"
                    onError={e => {
                      const width = template.orientacao === 'P' ? '120' : '70';
                      const height = template.orientacao === 'P' ? '80' : '100';
                      e.target.src = `data:image/svg+xml;base64,${btoa(`
                        <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect width="${width}" height="${height}" fill="#F8F9FA"/>
                          <path d="M${width / 2} ${height * 0.6}C${
                        width / 2 - 10
                      } ${height * 0.6} ${width / 2 - 18} ${height * 0.52} ${
                        width / 2 - 18
                      } ${height * 0.4}C${width / 2 - 18} ${height * 0.28} ${
                        width / 2 - 10
                      } ${height * 0.2} ${width / 2} ${height * 0.2}C${
                        width / 2 + 10
                      } ${height * 0.2} ${width / 2 + 18} ${height * 0.28} ${
                        width / 2 + 18
                      } ${height * 0.4}C${width / 2 + 18} ${height * 0.52} ${
                        width / 2 + 10
                      } ${height * 0.6} ${width / 2} ${
                        height * 0.6
                      }Z" fill="#6C757D"/>
                          <text x="${width / 2}" y="${
                        height * 0.46
                      }" font-family="Arial" font-size="10" fill="white" text-anchor="middle">IMG</text>
                        </svg>
                      `)}`;
                    }}
                  />
                </td>
                <td className="fw-medium">
                  <span
                    className={`badge ${
                      template.orientacao === 'P' ? 'bg-primary' : 'bg-info'
                    } me-2`}
                  >
                    {template.orientacao === 'P' ? 'PAISAGEM' : 'RETRATO'}
                  </span>
                </td>
                <td className="text-center">
                  <div className="btn-group btn-group-sm" role="group">
                    <button
                      className={`btn btn-outline-primary ${
                        loadingImage ? 'disabled' : ''
                      }`}
                      onClick={() => handleViewImage(template)}
                      title="Visualizar imagem"
                      disabled={loadingImage}
                    >
                      {loadingImage ? (
                        <div
                          className="spinner-border spinner-border-sm"
                          role="status"
                        >
                          <span className="visually-hidden">Carregando...</span>
                        </div>
                      ) : (
                        <i className="bi bi-eye"></i>
                      )}
                    </button>
                    <button
                      className="btn btn-outline-danger"
                      onClick={() => handleDeleteClick(template)}
                      title="Excluir imagem"
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr style={{ height: '200px' }}>
              <td colSpan="3" className="text-center text-muted align-middle">
                <div className="d-flex flex-column justify-content-center align-items-center h-100">
                  <i className="bi bi-image fs-1 mb-3 text-muted"></i>
                  <p className="mb-0">Nenhuma imagem encontrada</p>
                  <small className="text-muted">
                    Clique em "Nova Imagem" para adicionar
                  </small>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </TableContainer>

      {/* Modal de Upload de Imagem */}
      <UploadImagemModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSuccess={handleUploadSuccess}
        appCliente={appCliente}
      />

      {/* Modal reutilizável para visualizar imagem */}
      <ImageViewModal {...modalProps} />

      <DefaultModal {...setDefaultModal} />
      <ConfirmModal {...setConfirmModal} />
        </PageContent>
      </ResponsiveShadow>
    </Layout>
  );
};

export default UploadImagem;
